// Reading files out of a drag-and-drop that may contain whole folders (and folders inside folders).
// DataTransfer items stop being readable once the drop event returns, so the entries are captured
// synchronously, before the first await.

type Entry = {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file?: (ok: (f: File) => void, err: (e: unknown) => void) => void;
  createReader?: () => { readEntries: (ok: (b: Entry[]) => void, err: (e: unknown) => void) => void };
};

export async function filesFromDrop(dt: DataTransfer): Promise<File[]> {
  const entries = Array.from(dt.items || [])
    .map((i) => (i as unknown as { webkitGetAsEntry?: () => Entry | null }).webkitGetAsEntry?.() ?? null)
    .filter((e): e is Entry => !!e);
  if (!entries.length) return visible(Array.from(dt.files || []));
  const out: File[] = [];
  async function walk(entry: Entry): Promise<void> {
    if (entry.isFile && entry.file) {
      out.push(await new Promise<File>((ok, err) => entry.file!(ok, err)));
    } else if (entry.isDirectory && entry.createReader) {
      const reader = entry.createReader();
      for (;;) {   // readEntries returns at most ~100 entries per call
        const batch = await new Promise<Entry[]>((ok, err) => reader.readEntries(ok, err));
        if (!batch.length) break;
        for (const e of batch) await walk(e);
      }
    }
  }
  for (const e of entries) await walk(e);
  return visible(out);
}

/** Drop hidden system files (.DS_Store and friends). */
export function visible(files: File[]): File[] {
  return files.filter((f) => !f.name.startsWith("."));
}
